<template>
    <div v-if="showRow" class="table-filters-bar">
        <div class="table-filters-bar__pills d-flex flex-wrap align-center">
            <v-tooltip v-if="activeViewLabel" bottom open-delay="300">
                <template v-slot:activator="{ on, attrs }">
            <button
                type="button"
                class="table-filter-pill table-filter-pill--view"
                v-bind="attrs"
                v-on="on"
                :aria-label="activeViewLabel"
            >
                <span class="table-filter-pill__label">{{ activeViewLabel }}</span>
                <span
                    class="table-filter-pill__remove"
                    role="button"
                    tabindex="0"
                    :aria-label="$translate('removeView')"
                    @click.stop="$emit('clear-view')"
                    @keyup.enter.stop="$emit('clear-view')"
                >
                    <v-icon x-small>mdi-close</v-icon>
                </span>
            </button>
                </template>
                <span>{{ $translate("savedView") }}</span>
            </v-tooltip>

            <v-tooltip
                v-for="product in selectedProducts"
                :key="'product-' + product.id"
                bottom
                open-delay="300"
            >
                <template v-slot:activator="{ on, attrs }">
            <button
                type="button"
                class="table-filter-pill table-filter-pill--active"
                v-bind="attrs"
                v-on="on"
                :aria-label="productFilterLabel(product)"
            >
                <span class="table-filter-pill__label">{{
                    productFilterLabel(product)
                }}</span>
                <span
                    class="table-filter-pill__remove"
                    role="button"
                    tabindex="0"
                    :aria-label="removeProductLabel"
                    @click.stop="$emit('remove-product', product.id)"
                    @keyup.enter.stop="$emit('remove-product', product.id)"
                >
                    <v-icon x-small>mdi-close</v-icon>
                </span>
            </button>
                </template>
                <span>{{ $translate("productFilter") }}</span>
            </v-tooltip>

            <TableFilterPill
                v-for="conf in leadingActiveFilterConfigs"
                :key="'active-' + conf.field"
                :filter="conf"
                :filters="filters"
                :choices="choicesFor(conf)"
                :remove-label="$translate('removeFilter')"
                @change="onPillChange(conf, $event)"
            />

            <template v-if="panelOpen">
                <TableFilterPill
                    v-for="conf in inactiveQuickConfigs"
                    :key="'quick-' + conf.field"
                    :filter="conf"
                    :filters="filters"
                    :choices="choicesFor(conf)"
                    :remove-label="$translate('removeFilter')"
                    @change="onPillChange(conf, $event)"
                />

                <TableFilterPill
                    v-for="conf in extraAddedFilterConfigs"
                    :key="'added-' + conf.field"
                    :filter="conf"
                    :filters="filters"
                    :choices="choicesFor(conf)"
                    :default-open="isPendingAddFilter(conf)"
                    :remove-label="$translate('removeFilter')"
                    @change="onAddedPillChange(conf, $event)"
                    @menu-close="onAddedPillMenuClose(conf)"
                />
            </template>

            <div
                v-if="showAddFilterControls"
                class="table-filters-bar__actions d-flex align-center"
            >
                <v-menu
                    v-if="addableFilters.length"
                    v-model="addMenuOpen"
                    offset-y
                    transition="scale-transition"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <button
                            type="button"
                            class="table-filter-pill table-filter-pill--add"
                            v-bind="attrs"
                            v-on="on"
                            :aria-label="$translate('addFilter')"
                        >
                            <v-icon x-small class="mr-1">mdi-plus</v-icon>
                            {{ $translate("addFilter") }}
                        </button>
                    </template>
                    <v-list dense>
                        <v-list-item
                            v-for="conf in addableFilters"
                            :key="'add-' + conf.field"
                            @click="openAddedFilter(conf)"
                        >
                            <v-list-item-title>{{ conf.label }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <span
                    v-if="canReset"
                    class="table-filters-bar__reset link"
                    role="button"
                    tabindex="0"
                    @click="onReset"
                    @keyup.enter="onReset"
                >
                    {{ $translate("resetFilters") }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import TableFilterPill from "./table-filter-pill";
import {
    booleanFilterApplyValue,
    filterHasValue,
    getAddableFilterConfigs,
    getQuickFilterConfigs,
    isBooleanFilter,
    normalizeActiveFilterValue,
} from "@/utils/tableFilterLabels";

export default {
    name: "TableFiltersBar",
    components: { TableFilterPill },
    props: {
        filters: {
            type: Object,
            required: true,
        },
        fullFiltersConf: {
            type: Array,
            default: () => [],
        },
        panelOpen: {
            type: Boolean,
            default: false,
        },
        activeViewLabel: {
            type: String,
            default: null,
        },
        quickFilterCount: {
            type: Number,
            default: 3,
        },
        filterChoices: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            addMenuOpen: false,
            pendingAddFilter: null,
            pendingMenuOpen: false,
            extraVisibleFields: [],
        };
    },
    computed: {
        nonSearchFilters() {
            return (this.fullFiltersConf || []).filter((c) => c.type !== "search");
        },
        activeFilterConfigs() {
            return this.nonSearchFilters.filter((conf) =>
                filterHasValue(conf, this.filters),
            );
        },
        /**
         * Active pills shown before quick/inactive ones. Filters added via
         * "+ Add filter" stay in the trailing group even after they get a value.
         * Multi-select quick filters also stay in the quick strip so choosing
         * the first value does not remount the open menu.
         */
        leadingActiveFilterConfigs() {
            const stickyQuickFields = new Set(
                this.quickFilterConfigs
                    .filter((conf) => conf.multiple)
                    .map((conf) => conf.field),
            );
            if (!this.panelOpen || !this.extraVisibleFields.length) {
                if (!this.panelOpen) {
                    return this.activeFilterConfigs;
                }
                return this.activeFilterConfigs.filter(
                    (conf) => !stickyQuickFields.has(conf.field),
                );
            }
            const trailingFields = new Set(this.extraVisibleFields);
            return this.activeFilterConfigs.filter(
                (conf) =>
                    !trailingFields.has(conf.field)
                    && !stickyQuickFields.has(conf.field),
            );
        },
        quickFilterConfigs() {
            return getQuickFilterConfigs(this.fullFiltersConf, {
                maxCount: this.quickFilterCount,
            });
        },
        visibleQuickFields() {
            return [
                ...this.quickFilterConfigs.map((conf) => conf.field),
                ...this.extraAddedFilterConfigs.map((conf) => conf.field),
                ...this.activeFilterConfigs.map((conf) => conf.field),
            ];
        },
        /**
         * Quick filters without a value, plus multi-select quicks (even when
         * active) so the pill instance / open menu stays mounted while
         * selecting several options.
         */
        inactiveQuickConfigs() {
            return this.quickFilterConfigs.filter((conf) => {
                if (!this.shouldDisplayFilter(conf)) {
                    return false;
                }
                if (conf.multiple) {
                    return true;
                }
                return !filterHasValue(conf, this.filters);
            });
        },
        /**
         * Filters opened via "+ Add filter", in addition order (newest last).
         * Kept here after a value is chosen so they do not jump before inactive quicks.
         */
        extraAddedFilterConfigs() {
            const byField = new Map(
                this.nonSearchFilters.map((conf) => [conf.field, conf]),
            );
            const quickFields = new Set(
                this.quickFilterConfigs.map((conf) => conf.field),
            );
            return this.extraVisibleFields
                .map((field) => byField.get(field))
                .filter(
                    (conf) =>
                        !!conf &&
                        !quickFields.has(conf.field) &&
                        this.shouldDisplayFilter(conf),
                );
        },
        addableFilters() {
            return getAddableFilterConfigs(
                this.fullFiltersConf,
                this.filters,
                [...this.visibleQuickFields],
            ).filter((conf) => this.shouldDisplayFilter(conf));
        },
        selectedProducts() {
            return Array.isArray(this.filters?.products) ? this.filters.products : [];
        },
        removeProductLabel() {
            return this.$translate("removeProduct");
        },
        canReset() {
            return (
                this.panelOpen &&
                (this.activeFilterConfigs.length > 0 ||
                    this.selectedProducts.length > 0)
            );
        },
        showAddFilterControls() {
            return this.panelOpen;
        },
        showRow() {
            if (
                this.activeViewLabel ||
                this.activeFilterConfigs.length > 0 ||
                this.selectedProducts.length > 0
            ) {
                return true;
            }
            return this.panelOpen;
        },
    },
    methods: {
        productFilterLabel(product) {
            if (!product) {
                return "";
            }
            if (product.upc && product.name) {
                return `${product.upc} — ${product.name}`;
            }
            return product.name || product.upc || String(product.id);
        },
        choicesFor(conf) {
            return this.filterChoices[conf.field] || conf.choices || [];
        },
        isPendingAddFilter(conf) {
            return (
                this.pendingMenuOpen &&
                this.pendingAddFilter?.field === conf.field
            );
        },
        shouldDisplayFilter(filterconf) {
            if (typeof filterconf.display === "function") {
                return filterconf.display(this.filters);
            }
            return true;
        },
        onPillChange(conf, value) {
            if (this.isFilterCleared(value)) {
                this.removeExtraVisibleField(conf.field);
            }
            this.$emit("filter-change", { filter: conf, value });
        },
        openAddedFilter(conf) {
            this.addMenuOpen = false;
            if (!this.extraVisibleFields.includes(conf.field)) {
                this.extraVisibleFields = [...this.extraVisibleFields, conf.field];
            }
            if (isBooleanFilter(conf)) {
                this.onPillChange(conf, booleanFilterApplyValue(conf));
                return;
            }
            this.pendingAddFilter = conf;
            this.$nextTick(() => {
                this.pendingMenuOpen = true;
            });
        },
        onAddedPillChange(conf, value) {
            this.onPillChange(conf, value);
            if (conf?.multiple) {
                const normalized = normalizeActiveFilterValue(value, conf);
                const hasValue =
                    normalized !== null &&
                    normalized !== undefined &&
                    normalized !== "" &&
                    (!Array.isArray(normalized) || normalized.length > 0);
                if (hasValue) {
                    this.clearPendingAdd(conf);
                }
                return;
            }
            if (value === null || value === undefined || value === "") {
                return;
            }
            if (
                typeof value === "object" &&
                !Array.isArray(value) &&
                ("min" in value || "max" in value)
            ) {
                if (value.min == null && value.max == null) {
                    return;
                }
            }
            // Keep the pill in the trailing "added" group (after inactive quicks).
            this.clearPendingAdd(conf);
        },
        onAddedPillMenuClose(conf) {
            if (this.pendingAddFilter?.field !== conf.field) {
                return;
            }
            this.clearPendingAdd(conf);
            if (!filterHasValue(conf, this.filters)) {
                this.removeExtraVisibleField(conf.field);
            }
        },
        clearPendingAdd(conf) {
            if (this.pendingAddFilter?.field !== conf?.field) {
                return;
            }
            this.pendingAddFilter = null;
            this.pendingMenuOpen = false;
        },
        isFilterCleared(value) {
            if (value === null || value === undefined || value === "") {
                return true;
            }
            if (
                typeof value === "object" &&
                !Array.isArray(value) &&
                ("min" in value || "max" in value)
            ) {
                return value.min == null && value.max == null;
            }
            return false;
        },
        removeExtraVisibleField(field) {
            if (!this.extraVisibleFields.includes(field)) {
                return;
            }
            this.extraVisibleFields = this.extraVisibleFields.filter(
                (entry) => entry !== field,
            );
        },
        onReset() {
            this.extraVisibleFields = [];
            this.pendingAddFilter = null;
            this.pendingMenuOpen = false;
            this.$emit("reset");
        },
    },
    i18n: {
        messages: {
            en: {
                addFilter: "Add filter",
                resetFilters: "Reset filters",
                removeFilter: "Remove filter",
                removeView: "Remove saved view",
                removeProduct: "Remove product",
                savedView: "Saved view",
                productFilter: "Product",
            },
            fr: {
                addFilter: "Ajouter un filtre",
                resetFilters: "Réinitialiser les filtres",
                removeFilter: "Retirer le filtre",
                removeView: "Retirer la vue",
                removeProduct: "Retirer le produit",
                savedView: "Vue enregistrée",
                productFilter: "Produit",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";
@import "@/assets/css/table-filter-pill";

.table-filters-bar {
    margin-top: 10px;

    &__pills {
        gap: 8px;
    }

    &__actions {
        gap: 8px;
        order: 1;
    }

    &__reset {
        font-size: 13px;
        white-space: nowrap;
        cursor: pointer;
        text-decoration: underline;
    }
}
</style>
